import { memo, useContext, useMemo } from 'react';
import { EnumCellProps, WithClassname } from '@jsonforms/core';

import { TranslateProps, useJsonForms } from '@jsonforms/react';
import { MuiAutocomplete } from '@jsonforms/material-renderers';
import { Autocomplete as FreeSoloAutocomplete, Box, TextField, Tooltip, Typography } from '@mui/material';
import get from 'lodash/get';
import { PropIssuesContext } from '../cellPropTextField/context';

export interface WithInputProps {
  label?: string;
}

export const i18nDefaults = {
  'enum.none': 'None',
};

const Autocomplete = (props: EnumCellProps & WithClassname & TranslateProps & WithInputProps) => {
  const {
    data,
    className,
    id,
    enabled,
    schema,
    uischema,
    path,
    handleChange,
    options,
    config,
    label,
    t,
    errors,
    rootSchema,
  } = props;

  const ctx = useJsonForms();
  const effectiveErrors = useMemo(() => {
    const lines = String(errors || '')
      .split('\n')
      .map((line) => String(line || '').trim())
      .filter(Boolean);

    const uniqueLines = Array.from(new Set(lines));
    const scope = String(get(uischema, 'scope', '')).trim();
    const isReadOrIdField = /\/trigger$/.test(scope) || /\/id$/.test(scope);
    const hasEnumViolation = uniqueLines.some((line) =>
      line.toLowerCase().includes('must be equal to one of the allowed values')
    );

    if (isReadOrIdField && hasEnumViolation) {
      const selectedValue = String(data ?? '').trim();
      return selectedValue
        ? `Invalid value: "${selectedValue}".`
        : 'Invalid value.';
    }

    return uniqueLines[0] || '';
  }, [data, errors, uischema]);

  const propIssues = useContext(PropIssuesContext);
  const parts = path.split('.');
  const propIndex = parseInt(parts[parts.length - 2], 10);
  const fieldName = parts[parts.length - 1];
  const contextIssue = propIssues.find(
    (issue: any) =>
      issue.propIndex === propIndex &&
      (issue.editableParamField === fieldName || (issue.editableField === 'prop_id' && fieldName === 'id'))
  );
  const inlineError = contextIssue?.shortText || contextIssue?.text || '';
  const displayError = effectiveErrors || inlineError;
  const isValid = !Boolean(displayError);

  // Free-text mode: no enum options — render freeSolo Autocomplete with PropIssuesContext errors.
  const isFreeText = !Array.isArray(options) || options.length === 0;
  if (isFreeText) {
   const freeTextError = displayError;
    return (
      <Box>
        <FreeSoloAutocomplete
          freeSolo
          open={false}
          options={[]}
          value={data ?? ''}

          disabled={!enabled}
          onChange={(_e: any, newValue: any) => handleChange(path, (newValue as string) || undefined)}
          onInputChange={(_e: any, newValue: string, reason: string) => {
            if (reason === 'input') handleChange(path, newValue || undefined);
            if (reason === 'clear') handleChange(path, undefined);
          }}
          renderInput={(params: any) => (
            <TextField {...params} error={Boolean(freeTextError)} size='small' />
          )}
        />
        {freeTextError && (
          <Typography
            component='p'
            sx={{ color: '#D32F2F', fontSize: '0.75rem', lineHeight: 1.2, mt: 0.25, display: 'block' }}
          >
            {freeTextError}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box >
      <MuiAutocomplete
        data={data}
        uischema={uischema}
        errors=""
        label={label || ''}
        rootSchema={rootSchema}
        id={id}
        schema={schema}
        enabled={enabled}
        visible={props.visible}
        path={path}
        className={props.uischema.scope.includes('id') ? 'idAutoComplete' : 'dataTypeAutoComplete'}
        handleChange={(path, value) => {
          const [_messages, message_idx, _props, prop_idx, field] = path.split('.');
          handleChange(path, value);
          if (field === 'id') {
            const dataType = get(ctx, 'core.data.scan_result', []).find((res: any) => res.id === value);
            handleChange(
              [_messages, message_idx, _props, prop_idx, 'datatype'].join('.'),
              value && dataType && dataType.datatype ? dataType.datatype : undefined
            );
          }
        }}
        isValid={isValid}
        options={options || []}
        renderOption={(props, option) => {
          return (
            <Typography {...props} key={props.id} component='span'>
              <Tooltip title={option.label} arrow placement='left'>
                <Typography
                  component='p'
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {option.label}
                </Typography>
              </Tooltip>
            </Typography>
          );
        }}
      />
      {!isValid && displayError ? (
        <Typography
          component='p'
          sx={{ color: '#D32F2F', fontSize: '0.75rem', lineHeight: 1.2, mt: 0.25, display: 'block' }}
        >
          {displayError}
        </Typography>
      ) : null}
    </Box>
  );
};

export default memo(Autocomplete);
